import { faGasPump } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { selectState as selectStateAuth } from "authentication/main.slice";
import Loader from "components/Loader/Loader";
import Popover from "components/Popover/Popover";
import ToolboxButton, { ToolboxButtonIcon } from "Dashboard/components/Buttons/ToolboxButton";
import DateSelector from "Dashboard/components/DateSelector";
import { IUpdateRefuel } from "Dashboard/scenes/Resources/domain/IUpdateRefuel";
import { RESOURCES_MODULE_NAME } from "Dashboard/scenes/Resources/domain/ResourcesModule";
import { unitNameLabel } from "Dashboard/scenes/Resources/helpers/resourceUtil";
import { useAppDispatch } from "hooks/hooks";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { FormatDateIso, FormatDateShort } from "utils/dateUtils";
import { FormatNumberOrNull, FormatNumberWithUnitOrNull, getAdjustedFormatForUnit } from "utils/numberUtils";
import { selectResourceDetails } from "../../Details/details.slice";
import {
  clearFuelMap,
  getFuelsAndLubricants,
  requestDeletingRefuel,
  selectState,
  setAverageConsumptionPopover,
  setFuelLuricantsPopover,
  updateRefuel,
  updateRefuelTemporarily,
} from "../fuelsAndLubricants.slice";
import AverageConsumptionPopover from "./AverageConsumptionPopover";
import FuelLubricantsPopover from "./FuelLubricantsPopover";
import NumberInput from "Dashboard/components/NumberInput";
import DeleteRefuelModal from "./DeleteRefuelModal";

interface IProps {
  resourceId: number;
}

const ResourceFuelAndLubricants = ({ resourceId }: IProps) => {
  const dispatch = useAppDispatch();
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const resourceDetails = useSelector(selectResourceDetails);
  const isResourceActive = resourceDetails?.resource.status === "ACTIVE"

  const {
    loading,
    refuels,
    fuelLubricantsPopover,
    averageConsumptionPopover,
    totalKilometers,
    totalLiters,
  } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);

  const GetRefuels = useCallback(() => {
    if (dateFrom != null && dateTo != null && resourceId) {
      dispatch(
        getFuelsAndLubricants({
          resourceId,
          dateFrom: FormatDateIso(dateFrom),
          dateTo: FormatDateIso(dateTo),
        })
      );
    }
  }, [dispatch, dateFrom, dateTo, resourceId]);

  useEffect(() => {
    GetRefuels();
  }, [GetRefuels]);

  const selectDate = useCallback((data: { dateFrom: Date; dateTo: Date }) => {
    setDateFrom(data.dateFrom);
    setDateTo(data.dateTo);
  }, []);

  const onChangeBasicField = (data: IUpdateRefuel) => {
    dispatch(updateRefuelTemporarily(data));
    updateBasicFieldWithDelay(data);
  };

  const updateBasicFieldWithDelay = useMemo(
    () => debounce(async (data: IUpdateRefuel) => {
      try {
        await dispatch(updateRefuel(data)).unwrap();
        toast.success("Izmena je uspešno zabeležena.");
      } catch (status: any) {
        if (status === 403) {
          toast.error("Neočekivana greška");
        } else if (status === 400) {
          toast.error("Neočekivana greška");
        }
      }
    }, 1000),
    [dispatch]
  );

  return (
    <div className="col-3 loading-container">
      {(!resourceId || loading) && <Loader />}
      <div className="resources-box-header">
        <ToolboxButton
          icon={ToolboxButtonIcon.Add}
          tooltip={!resourceDetails?.isRefuelAllowed && hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
            ? "Gorivo se može sipati samo u resurse koji su zaduženi ili mere kilometražu/sate"
            : undefined
          }
          onClick={resourceDetails?.isRefuelAllowed && hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
            ? (e) => dispatch(
              setFuelLuricantsPopover({
                isOpen: true,
                top: e.clientY,
                left: e.clientX,
              })
            )
            : undefined
          }
        />
        <span className="title">GORIVO</span>
        <span
          className="icon-item"
          onClick={(e) =>
            dispatch(
              setAverageConsumptionPopover({
                isOpen: true,
                top: e.clientY,
                left: e.clientX,
              })
            )
          }
        >
          <FontAwesomeIcon className="fas" icon={faGasPump} />
        </span>
      </div>
      <div className="resources-box-sub-header">
        <span>Količina / KM_H</span>
        <DateSelector selectDate={selectDate} />
      </div>
      <div className="resources-box-list">
        <div className="resource-wrapp">
          {refuels.map((refuel, index) =>
            refuel.consumables.map((consumable) => (
              <div className="secondResource" key={index}>
                <span className="name">- {consumable.name}</span>
                <span className="buttons">
                  {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                    ? (<ToolboxButton
                      icon={ToolboxButtonIcon.Delete}
                      tooltip="Obriši"
                      noCircle={true}
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(requestDeletingRefuel(refuel));
                      }}
                    />)
                    : undefined}
                </span>
                <span className="date">
                  {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                    ? (<DatePicker
                      onChange={(date) =>
                        onChangeBasicField({ resourceId: refuel.resource.resourceId, refuelId: refuel.refuelId, date: date ? date.toISOString().split("T")[0] : undefined })
                      }
                      showMonthDropdown
                      showYearDropdown
                      dateFormat="dd.MM.yy"
                      className="date"
                      selected={refuel.date ? new Date(refuel.date) : null}
                    />)
                    : FormatDateShort(refuel.date)
                  }
                </span>
                <span className="unit1">litara</span>
                <span className="number1">{FormatNumberOrNull(consumable.quantityAmount, getAdjustedFormatForUnit('litara'))}</span>
                <span className="unit2">{unitNameLabel(refuel.usageUnit)}</span>
                <span className="number2">
                  {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                    ? <NumberInput
                      formatAs="quantity"
                      value={`${refuel.usageAmount}`}
                      onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                        if (parsedValue !== refuel.usageAmount) {
                          onChangeBasicField({ resourceId: refuel.resource.resourceId, refuelId: refuel.refuelId, usageAmount: parsedValue })
                        }
                      }}
                    />
                    : FormatNumberOrNull(refuel.usageAmount, getAdjustedFormatForUnit(refuel.usageUnit?.name))}
                </span>
                <input
                  type="text"
                  value={refuel.note || ""}
                  onChange={(e) => {
                    onChangeBasicField({ resourceId: refuel.resource.resourceId, refuelId: refuel.refuelId, note: e.target.value })
                  }}
                  className="note"
                  disabled={!(hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive)}
                />
              </div>
            ))
          )}
          {refuels.length < 4 && <div className="secondResource"></div>}
          {refuels.length < 3 && <div className="secondResource"></div>}
          {refuels.length < 2 && <div className="secondResource"></div>}
          {refuels.length < 1 && <div className="secondResource"></div>}
        </div>
      </div>
      <div className="secondFooter">
        <span className="w60">{FormatNumberWithUnitOrNull(totalKilometers, 'km')}</span>
        <span className="w30">{FormatNumberWithUnitOrNull(totalLiters, 'litara')}</span>
      </div>
      <Popover
        isOpen={averageConsumptionPopover.isOpen}
        onClose={() => {
          dispatch(setAverageConsumptionPopover({ isOpen: false }));
        }}
        title="Prosečna potrošnja"
        direction="right"
        arrowStyle={{ top: "50%" }}
        position={{
          top: -641,
          left: 977,
          offsetByWindowSizeY: true
        }}
      >
        <AverageConsumptionPopover />
      </Popover>
      <Popover
        isOpen={fuelLubricantsPopover.isOpen}
        onClose={() => {
          dispatch(setFuelLuricantsPopover({ isOpen: false }));
          dispatch(clearFuelMap());
        }}
        title="Gorivo - Maziva - Usluge"
        direction="right"
        arrowStyle={{ top: "243px" }}
        position={{
          top: -645,
          left: 748,
          offsetByWindowSizeY: true
        }}
      >
        <FuelLubricantsPopover />
      </Popover>
      <DeleteRefuelModal />
    </div>
  );
};

export default ResourceFuelAndLubricants;
